import { useMediaQuery, useTheme } from "@mui/material";
import { useLayoutStore } from "@stores/layoutStore";
import { useProjectStore } from "@stores/projectStore";
import { useMatchRoute } from "@tanstack/react-router";

/**
 * Effective render mode for the app's primary navigation rail.
 *
 *   "drawer"   — phone; rendered as a temporary overlay drawer
 *   "rail"     — narrow strip with icon-only nav (collapsible)
 *   "expanded" — full-width sidebar with labels
 *
 * Resolution order (highest precedence first):
 *   1. Phone breakpoint → always drawer.
 *   2. User pin override (expanded / rail) on tablet+desktop.
 *   3. Auto rule → rail. We collapse by default everywhere because the
 *      working surface (Workbench, PCB, Schematic) deserves the pixels;
 *      hover-peek gives one-flick access to labels when the user needs
 *      to navigate, and the pin toggle is one click away for users who
 *      want the labels permanently.
 */
export type AppNavMode = "drawer" | "rail" | "expanded";
export type ChatHistoryMode = "drawer" | "rail" | "expanded";

export function useIsChatSurface(): boolean {
  const matchRoute = useMatchRoute();
  return (
    !!matchRoute({ to: "/chat" }) ||
    !!matchRoute({ to: "/projects/$projectId/chat" })
  );
}

/** Project surfaces that host the Copilot rail. Workbench is the
 *  AI artefact home (HardwareArchitect agent); Requirements is the
 *  spec authoring surface (RequirementsAssistant agent). Other
 *  surfaces (PCB, Schematic, BOM, Cables) stay focused on inspection
 *  and don't render the rail.
 *
 *  The rail itself is the same `CopilotRail` component on both —
 *  surface awareness is server-side (the chat runner picks the agent
 *  from `surface` on the request body), so the UI doesn't need to
 *  swap components.
 */
function useIsCopilotSurface(): boolean {
  const matchRoute = useMatchRoute();
  return (
    !!matchRoute({ to: "/projects/$projectId/workbench" }) ||
    !!matchRoute({ to: "/projects/$projectId/requirements" })
  );
}

/** The surface key the chat sends to the backend so it can pick the
 *  right agent. Mirrors the backend `ChatSurface` literal union
 *  (`workbench` | `requirements`). Returns `null` outside of a
 *  Copilot-bearing surface.
 */
export type ChatSurfaceKey = "workbench" | "requirements";

export function useChatSurface(): ChatSurfaceKey | null {
  const matchRoute = useMatchRoute();
  if (matchRoute({ to: "/projects/$projectId/requirements" })) {
    return "requirements";
  }
  if (matchRoute({ to: "/projects/$projectId/workbench" })) {
    return "workbench";
  }
  // Full /chat surface: defaults to workbench (the original / wider
  // toolset). The user is already in the dedicated chat page, so
  // there's no surface signal to override the default.
  return null;
}

export function useAppNavMode(): AppNavMode {
  const theme = useTheme();
  // Phone — anything below sm. Tablets (iPad portrait ≥ 600) get a rail.
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const pin = useLayoutStore((s) => s.appNavPin);

  if (isPhone) return "drawer";
  if (pin === "expanded") return "expanded";
  // Default (auto + explicit rail) collapses to the rail; users who want
  // labels permanently click the pin toggle in the brand row.
  return "rail";
}

export function useChatHistoryMode(): ChatHistoryMode {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const pin = useLayoutStore((s) => s.chatHistoryPin);
  // Chat history is only rendered inside chat surfaces — its mode reflects
  // a question the caller has already answered ("we're in chat") so we
  // don't gate on route here.
  if (isPhone) return "drawer";
  if (pin === "expanded") return "expanded";
  if (pin === "rail") return "rail";
  // auto — collapse to rail on chat surfaces (the whole reason the user
  // clicked into chat is the chat itself, not picking another session).
  return "rail";
}

/**
 * Effective render state for the project-scoped Copilot right rail.
 *
 *   "hidden" — don't render the rail or its edge tab at all
 *   "open"   — full 380px column with conversation
 *   "closed" — slim edge tab that re-opens on click
 *
 * Scope: the rail is the Workbench's conversational companion — it
 * lives where the artefacts land. Other project surfaces (PCB,
 * Schematic, BOM, Cables, Sync) do NOT show the rail; users navigate
 * to the Workbench tab when they want to talk to the copilot. This
 * keeps the working surfaces focused and avoids conflating "viewing
 * the design" with "discussing the design."
 *
 * Resolution order:
 *   1. Phone breakpoint → hidden (no room for a permanent rail).
 *   2. Inside a chat surface → hidden (the page IS the conversation).
 *   3. No active project → hidden (chat is project-scoped today).
 *   4. Not on the Workbench surface → hidden.
 *   5. User pin override (open / closed) wins on tablet+desktop.
 *   6. Default → open.
 */
export type CopilotRailMode = "hidden" | "open" | "closed";

export function useCopilotRailMode(): CopilotRailMode {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  // Auto: closed on tablet (overlay would crowd the workbench until the
  // user calls for it), open on desktop where the 380px column doesn't
  // compromise the working surface.
  const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down("md"));
  const isChat = useIsChatSurface();
  const isCopilotSurface = useIsCopilotSurface();
  const pin = useLayoutStore((s) => s.copilotRailPin);
  const activeProject = useProjectStore((s) => s.activeProject);

  if (isPhone) return "hidden";
  if (isChat) return "hidden";
  if (!activeProject) return "hidden";
  if (!isCopilotSurface) return "hidden";
  if (pin === "open") return "open";
  if (pin === "closed") return "closed";
  return isTabletOrSmaller ? "closed" : "open";
}

/**
 * Layout placement for the open Copilot rail:
 *
 *   "docked"  — flex column reserves the rail's width (push-aside)
 *   "overlay" — rail floats above content; main width never changes
 *
 * Tablets (sm only) get the overlay so toggling the rail doesn't reflow
 * the workbench. Desktop (md+) keeps the docked rail because there's
 * room for both copilot and content.
 */
export type CopilotRailPlacement = "docked" | "overlay";

export function useCopilotRailPlacement(): CopilotRailPlacement {
  const theme = useTheme();
  const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down("md"));
  return isTabletOrSmaller ? "overlay" : "docked";
}
